import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      nav: {
        features: "Features",
        howItWorks: "How It Works",
        language: "Language",
      },
      beta: {
        message: "You're currently on PropGauge in beta.",
        cta: "Click here to return to RealtyBlocks",
      },
      hero: {
        titleLine1: "Get Your Instant",
        titleHighlight: "AI-Powered",
        titleLine2: "Property Valuation",
        subtitle:
          "Discover the true value of your property in real-time with PropGauge. Our AI-driven tool provides accurate and instant property valuations, helping you make informed decisions quickly.",
        ctaPrimary: "Get Your Free Property Valuation Now",
        ctaSecondary: "Discover RealtyBlocks Today",
        property1Title: "Modern Urban Apartment",
        property1Location: "Mumbai, Maharashtra",
        property2Title: "Luxury Villa",
        property2Location: "Gurgaon, Delhi NCR",
        property3Title: "Contemporary Home",
        property3Location: "Bangalore, Karnataka",
        aiMatch: "AI Match",
      },
      howItWorks: {
        title: "How RealtyBlocks Works",
        subtitle:
          "Experience the future of real estate with our AI-powered platform that simplifies every step of your property journey.",
        step1Title: "AI-Powered Search",
        step1Desc:
          "Our AI understands your preferences and finds the perfect properties that match your lifestyle, budget, and location requirements.",
        step2Title: "OfferChain™ Negotiations",
        step2Desc:
          "Transparent, real-time negotiations with AI-assisted pricing and instant communication between buyers and sellers.",
        step3Title: "AgreeMate™ Contracts",
        step3Desc:
          "Automatically generated, RERA-compliant contracts with built-in legal compliance and seamless digital signatures.",
      },
      testimonials: {
        title: "What Our Users Say",
        subtitle:
          "Join thousands of satisfied buyers, sellers, and service providers who have transformed their real estate experience with RealtyBlocks.",
        t1Role: "Home Buyer, Mumbai",
        t1Quote:
          "RealtyBlocks' AI found me the perfect apartment in just 2 weeks! The OfferChain™ feature made negotiations so transparent and stress-free.",
        t2Role: "Property Seller, Bangalore",
        t2Quote:
          "Sold my villa 40% faster than expected! The SmartBid™ system helped me get the best price with qualified buyers only.",
        t3Role: "Interior Designer, Delhi",
        t3Quote:
          "ServiceMate™ connected me with premium clients. My business grew 3x in 6 months with verified leads and automated payments!",
      },
      cta: {
        title: "Ready to Transform Your Real Estate Experience?",
        subtitle:
          "Join thousands of users who are already experiencing the future of real estate with AI-powered tools and seamless transactions.",
        startJourney: "Start Your Journey",
        scheduleDemo: "Schedule Demo",
        statUsers: "Happy Users",
        statTransactions: "Properties Transacted",
        statSatisfaction: "Customer Satisfaction",
      },
      footer: {
        tagline:
          "India's first AI-powered real estate platform revolutionizing property transactions with transparency, efficiency, and innovation.",
        platform: "Platform",
        forBuyers: "For Buyers",
        forSellers: "For Sellers",
        forServiceProviders: "For Service Providers",
        aiTools: "AI Tools",
        support: "Support",
        howWeCalculate: "How We Calculate",
        myValuations: "My Valuations",
        helpCenter: "Help Center",
        contactUs: "Contact Us",
        privacyPolicy: "Privacy Policy",
        termsOfService: "Terms of Service",
        copyright: "© 2024 RealtyBlocks. All rights reserved.",
        madeIn: "Made with ❤️ in India",
      },
      faq: {
        title: "Frequently Asked Questions",
        subtitle:
          "Everything you need to know about India's first AI-powered real estate platform",
        moreQuestions: "Have more questions about AI-powered property valuation?",
        contactExperts: "Contact Our Experts",
        items: [
          {
            q: "What is RealtyBlocks and how does it work?",
            a: "RealtyBlocks is India's first AI-powered real estate operating system that provides instant property valuations and comprehensive market analysis. Our advanced AI analyzes multiple data points including location, property type, age, amenities, and comparable sales to generate accurate property valuations in real-time.",
          },
          {
            q: "How accurate are RealtyBlocks AI property valuations?",
            a: "Our AI-powered valuations achieve up to 94% confidence scores by analyzing thousands of comparable properties, market trends, location premiums, and real-time data. However, these are AI-generated estimates for reference only, and we recommend consulting with our professional agents for final property decisions.",
          },
          {
            q: "Which cities does RealtyBlocks cover in India?",
            a: "RealtyBlocks covers all major Indian cities including Mumbai, Delhi, Bangalore, Pune, Hyderabad, Chennai, Kolkata, Ahmedabad, and many more. Our AI system is continuously expanding to cover tier-2 and tier-3 cities across India.",
          },
          {
            q: "What makes RealtyBlocks different from other property websites?",
            a: "RealtyBlocks uses advanced artificial intelligence and machine learning to provide instant, data-driven property valuations. Unlike traditional property portals, we offer comprehensive market analysis, investment insights, location analysis, and villa-specific land value breakdowns powered by AI technology.",
          },
          {
            q: "Is RealtyBlocks free to use?",
            a: "Yes, RealtyBlocks offers free AI-powered property valuations to all users. You can get instant property estimates, market analysis, and investment insights at no cost. We also provide detailed email reports for comprehensive property analysis.",
          },
          {
            q: "What information do I need for a property valuation?",
            a: "To get an accurate AI valuation, you need basic property details: property type (apartment, villa, etc.), number of bedrooms, built-up area in square feet, location, property age, and additional details like furnishing status, parking, and amenities.",
          },
          {
            q: "How does RealtyBlocks calculate villa land values?",
            a: "For villas and independent houses, our AI calculates separate land value and construction value components. The land value percentage varies by location (35-45% of total value) based on location premium, with detailed breakdowns showing land ownership benefits and development potential.",
          },
          {
            q: "Can I trust RealtyBlocks AI valuations for investment decisions?",
            a: "Our AI valuations provide excellent reference points based on comprehensive market data analysis. However, they should be used as starting points for your research. For final investment decisions, we strongly recommend consulting with RealtyBlocks professional agents who can provide personalized guidance.",
          },
        ],
      },
      valuation: {
        sectionTitle: "PropGauge™ Valuation Calculator",
        sectionSubtitle:
          "Get instant AI-powered property valuations with comprehensive Indian market analysis",
        propertyDetails: "Property Details",
        propertyType: "Property Type",
        selectType: "Select type",
        configuration: "Configuration",
        bhk: "BHK",
        carpetArea: "Carpet Area (sq ft)",
        ageYears: "Age (years)",
        age: "Age",
        location: "Location",
        locationPlaceholder: "Enter locality, city (e.g., Koramangala, Bangalore)",
        condition: "Property Condition",
        selectCondition: "Select condition",
        facing: "Facing Direction",
        selectFacing: "Select facing",
        email: "Email",
        emailPlaceholder: "your@email.com",
        getValuation: "Get Instant Valuation",
        emailReport: "Email Detailed Report",
        analyzing: "Analyzing...",
        sending: "Sending...",
        missingTitle: "Missing Information",
        missingDesc: "Please fill in: {{fields}}",
        emailRequiredTitle: "Email Required",
        emailRequiredDesc: "Please enter your email to receive the detailed report.",
        successTitle: "AI Valuation Complete!",
        successDesc:
          "Your property has been analyzed using advanced AI and real market data.",
        errorTitle: "Error",
        errorDesc: "Failed to calculate valuation. Please try again.",
      },
      cities: {
        explore: "Explore Properties",
        primeLocations: "Prime Locations in {{city}}",
        mumbai: {
          title: "Properties in Mumbai",
          tagline: "India's Financial Capital",
          subtitle:
            "Discover verified properties in Mumbai's prime locations. From luxury apartments in Bandra to modern flats in Powai, find your perfect home with AI-powered search and zero brokerage.",
          ctaButton: "Explore Mumbai Properties",
          metaTitle: "Property in Mumbai - Buy, Sell & Rent | RealtyBlocks",
          metaDesc:
            "Find verified properties in Mumbai. Buy apartments in Bandra, Andheri, Powai. Rent flats in Worli, Lower Parel. AI-powered search with zero brokerage.",
        },
        bangalore: {
          title: "Properties in Bangalore",
          tagline: "India's Silicon Valley",
          subtitle:
            "Discover verified properties in Bangalore's tech corridors. From modern apartments in Koramangala to luxury villas in Whitefield, find your perfect home with AI-powered search.",
          ctaButton: "Explore Bangalore Properties",
          metaTitle: "Property in Bangalore - Buy, Sell & Rent | RealtyBlocks",
          metaDesc:
            "Find verified properties in Bangalore. Buy apartments in Koramangala, Whitefield, HSR Layout. AI-powered search with zero brokerage.",
        },
        hyderabad: {
          title: "Properties in Hyderabad",
          tagline: "City of Pearls & Tech",
          subtitle:
            "Discover verified properties in Hyderabad's prime areas. From modern apartments in Gachibowli to luxury homes in Jubilee Hills, find your perfect home with AI-powered search.",
          ctaButton: "Explore Hyderabad Properties",
          metaTitle: "Property in Hyderabad - Buy, Sell & Rent | RealtyBlocks",
          metaDesc:
            "Find verified properties in Hyderabad. Buy apartments in Gachibowli, Madhapur, Hitech City. AI-powered search with zero brokerage.",
        },
      },
      seo: {
        homeTitle: "RealtyBlocks – India's AI-Powered Real Estate OS",
        homeDesc:
          "Discover, buy, sell, rent verified properties across India with AI-driven tools, instant deals, and trusted services for buyers, sellers, NRIs, and agents.",
      },
      legal: {
        viewHindi: "हिन्दी में पढ़ें",
        viewEnglish: "View in English",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookie Policy",
        disclaimer: "Disclaimer",
        refund: "Refund & Cancellation",
        grievance: "Grievance Redressal",
        security: "Security",
        contactDpo: "Contact our Grievance Officer / DPO",
      },
    },
  },
  hi: {
    translation: {
      nav: {
        features: "विशेषताएँ",
        howItWorks: "यह कैसे काम करता है",
        language: "भाषा",
      },
      beta: {
        message: "आप अभी PropGauge बीटा पर हैं।",
        cta: "RealtyBlocks पर वापस जाने के लिए यहाँ क्लिक करें",
      },
      hero: {
        titleLine1: "तुरंत प्राप्त करें",
        titleHighlight: "AI-संचालित",
        titleLine2: "प्रॉपर्टी मूल्यांकन",
        subtitle:
          "PropGauge के साथ अपनी प्रॉपर्टी का असली मूल्य रियल-टाइम में जानें। हमारा AI-आधारित टूल सटीक और तत्काल प्रॉपर्टी मूल्यांकन प्रदान करता है, जिससे आप तेज़ी से सही निर्णय ले सकें।",
        ctaPrimary: "अभी मुफ़्त प्रॉपर्टी मूल्यांकन प्राप्त करें",
        ctaSecondary: "आज ही RealtyBlocks खोजें",
        property1Title: "आधुनिक शहरी अपार्टमेंट",
        property1Location: "मुंबई, महाराष्ट्र",
        property2Title: "लक्ज़री विला",
        property2Location: "गुड़गांव, दिल्ली NCR",
        property3Title: "समकालीन घर",
        property3Location: "बैंगलोर, कर्नाटक",
        aiMatch: "AI मिलान",
      },
      howItWorks: {
        title: "RealtyBlocks कैसे काम करता है",
        subtitle:
          "हमारे AI-संचालित प्लेटफ़ॉर्म के साथ रियल एस्टेट का भविष्य अनुभव करें जो आपकी प्रॉपर्टी यात्रा के हर चरण को सरल बनाता है।",
        step1Title: "AI-संचालित खोज",
        step1Desc:
          "हमारा AI आपकी पसंद को समझता है और आपकी जीवनशैली, बजट और स्थान की आवश्यकताओं के अनुरूप उत्तम प्रॉपर्टी ढूँढ़ता है।",
        step2Title: "OfferChain™ बातचीत",
        step2Desc:
          "खरीदारों और विक्रेताओं के बीच पारदर्शी, रियल-टाइम बातचीत, AI-सहायता प्राप्त मूल्य निर्धारण और तत्काल संचार के साथ।",
        step3Title: "AgreeMate™ अनुबंध",
        step3Desc:
          "स्वतः उत्पन्न, RERA-अनुपालक अनुबंध जिनमें कानूनी अनुपालन और सहज डिजिटल हस्ताक्षर अंतर्निहित हैं।",
      },
      testimonials: {
        title: "हमारे उपयोगकर्ता क्या कहते हैं",
        subtitle:
          "हज़ारों संतुष्ट खरीदार, विक्रेता और सेवा प्रदाताओं में शामिल हों जिन्होंने RealtyBlocks के साथ अपना रियल एस्टेट अनुभव बदला है।",
        t1Role: "घर खरीदार, मुंबई",
        t1Quote:
          "RealtyBlocks के AI ने मुझे केवल 2 हफ़्तों में सही अपार्टमेंट दिला दिया! OfferChain™ ने बातचीत को बहुत पारदर्शी और तनाव मुक्त बनाया।",
        t2Role: "प्रॉपर्टी विक्रेता, बैंगलोर",
        t2Quote:
          "अपनी विला अनुमान से 40% तेज़ी से बेची! SmartBid™ ने योग्य खरीदारों के साथ सर्वश्रेष्ठ मूल्य दिलाने में मदद की।",
        t3Role: "इंटीरियर डिज़ाइनर, दिल्ली",
        t3Quote:
          "ServiceMate™ ने मुझे प्रीमियम क्लाइंट्स से जोड़ा। 6 महीनों में मेरा व्यवसाय 3 गुना बढ़ा, सत्यापित लीड्स और स्वचालित भुगतान के साथ!",
      },
      cta: {
        title: "क्या आप अपना रियल एस्टेट अनुभव बदलने के लिए तैयार हैं?",
        subtitle:
          "हज़ारों उपयोगकर्ताओं में शामिल हों जो AI-संचालित टूल और निर्बाध लेनदेन के साथ रियल एस्टेट का भविष्य अनुभव कर रहे हैं।",
        startJourney: "अपनी यात्रा शुरू करें",
        scheduleDemo: "डेमो शेड्यूल करें",
        statUsers: "खुश उपयोगकर्ता",
        statTransactions: "प्रॉपर्टी लेनदेन",
        statSatisfaction: "ग्राहक संतुष्टि",
      },
      footer: {
        tagline:
          "भारत का पहला AI-संचालित रियल एस्टेट प्लेटफ़ॉर्म जो पारदर्शिता, दक्षता और नवाचार के साथ प्रॉपर्टी लेनदेन में क्रांति ला रहा है।",
        platform: "प्लेटफ़ॉर्म",
        forBuyers: "खरीदारों के लिए",
        forSellers: "विक्रेताओं के लिए",
        forServiceProviders: "सेवा प्रदाताओं के लिए",
        aiTools: "AI टूल्स",
        support: "सहायता",
        howWeCalculate: "हम कैसे गणना करते हैं",
        myValuations: "मेरी मूल्यांकन",
        helpCenter: "सहायता केंद्र",
        contactUs: "संपर्क करें",
        privacyPolicy: "गोपनीयता नीति",
        termsOfService: "सेवा की शर्तें",
        copyright: "© 2024 RealtyBlocks. सर्वाधिकार सुरक्षित।",
        madeIn: "भारत में ❤️ से बना",
      },
      faq: {
        title: "अक्सर पूछे जाने वाले प्रश्न",
        subtitle:
          "भारत के पहले AI-संचालित रियल एस्टेट प्लेटफ़ॉर्म के बारे में आपको जो कुछ जानना चाहिए",
        moreQuestions: "AI-संचालित प्रॉपर्टी मूल्यांकन के बारे में और प्रश्न हैं?",
        contactExperts: "हमारे विशेषज्ञों से संपर्क करें",
        items: [
          {
            q: "RealtyBlocks क्या है और यह कैसे काम करता है?",
            a: "RealtyBlocks भारत का पहला AI-संचालित रियल एस्टेट ऑपरेटिंग सिस्टम है जो तत्काल प्रॉपर्टी मूल्यांकन और व्यापक मार्केट विश्लेषण प्रदान करता है। हमारा AI स्थान, प्रॉपर्टी प्रकार, आयु, सुविधाएँ और तुलनीय बिक्री जैसे कई डेटा बिंदुओं का विश्लेषण कर रियल-टाइम में सटीक मूल्यांकन देता है।",
          },
          {
            q: "RealtyBlocks AI मूल्यांकन कितने सटीक हैं?",
            a: "हमारे AI-संचालित मूल्यांकन हज़ारों तुलनीय प्रॉपर्टीज़, मार्केट ट्रेंड्स, स्थान प्रीमियम और रियल-टाइम डेटा का विश्लेषण कर 94% तक का विश्वास स्तर प्राप्त करते हैं। ये केवल संदर्भ हेतु AI-जनित अनुमान हैं; अंतिम निर्णय के लिए कृपया हमारे पेशेवर एजेंटों से परामर्श करें।",
          },
          {
            q: "RealtyBlocks भारत में किन शहरों को कवर करता है?",
            a: "RealtyBlocks मुंबई, दिल्ली, बैंगलोर, पुणे, हैदराबाद, चेन्नई, कोलकाता, अहमदाबाद और कई प्रमुख शहरों को कवर करता है। हमारा AI सिस्टम लगातार टियर-2 और टियर-3 शहरों तक विस्तार कर रहा है।",
          },
          {
            q: "RealtyBlocks अन्य प्रॉपर्टी वेबसाइटों से अलग क्यों है?",
            a: "RealtyBlocks उन्नत आर्टिफिशियल इंटेलिजेंस और मशीन लर्निंग का उपयोग कर तत्काल, डेटा-संचालित प्रॉपर्टी मूल्यांकन देता है। पारंपरिक पोर्टलों के विपरीत हम व्यापक मार्केट विश्लेषण, निवेश अंतर्दृष्टि और विला-विशिष्ट भूमि मूल्य विवरण प्रदान करते हैं।",
          },
          {
            q: "क्या RealtyBlocks उपयोग करने के लिए मुफ़्त है?",
            a: "हाँ, RealtyBlocks सभी उपयोगकर्ताओं को मुफ़्त AI-संचालित प्रॉपर्टी मूल्यांकन प्रदान करता है। आप बिना किसी शुल्क के तत्काल अनुमान, मार्केट विश्लेषण और निवेश अंतर्दृष्टि प्राप्त कर सकते हैं।",
          },
          {
            q: "प्रॉपर्टी मूल्यांकन के लिए मुझे कौन सी जानकारी चाहिए?",
            a: "सटीक AI मूल्यांकन के लिए आपको प्रॉपर्टी प्रकार, बेडरूम की संख्या, क्षेत्रफल (वर्ग फुट में), स्थान, प्रॉपर्टी की आयु, और फर्निशिंग, पार्किंग व सुविधाओं जैसे विवरण चाहिए।",
          },
          {
            q: "RealtyBlocks विला भूमि मूल्य की गणना कैसे करता है?",
            a: "विला और स्वतंत्र घरों के लिए हमारा AI भूमि मूल्य और निर्माण मूल्य अलग-अलग गणना करता है। स्थान प्रीमियम के अनुसार भूमि का मूल्य कुल मूल्य का 35-45% होता है।",
          },
          {
            q: "क्या मैं निवेश निर्णयों के लिए RealtyBlocks AI मूल्यांकन पर भरोसा कर सकता हूँ?",
            a: "हमारे AI मूल्यांकन व्यापक मार्केट डेटा पर आधारित बेहतरीन संदर्भ बिंदु हैं। अंतिम निवेश निर्णय के लिए कृपया RealtyBlocks के पेशेवर एजेंटों से परामर्श करें।",
          },
        ],
      },
      valuation: {
        sectionTitle: "PropGauge™ मूल्यांकन कैलकुलेटर",
        sectionSubtitle:
          "व्यापक भारतीय बाज़ार विश्लेषण के साथ तत्काल AI-संचालित प्रॉपर्टी मूल्यांकन प्राप्त करें",
        propertyDetails: "प्रॉपर्टी विवरण",
        propertyType: "प्रॉपर्टी प्रकार",
        selectType: "प्रकार चुनें",
        configuration: "कॉन्फ़िगरेशन",
        bhk: "BHK",
        carpetArea: "कार्पेट क्षेत्र (वर्ग फुट)",
        ageYears: "आयु (वर्ष)",
        age: "आयु",
        location: "स्थान",
        locationPlaceholder: "क्षेत्र, शहर दर्ज करें (जैसे, कोरमंगला, बैंगलोर)",
        condition: "प्रॉपर्टी स्थिति",
        selectCondition: "स्थिति चुनें",
        facing: "दिशा",
        selectFacing: "दिशा चुनें",
        email: "ईमेल",
        emailPlaceholder: "your@email.com",
        getValuation: "तत्काल मूल्यांकन प्राप्त करें",
        emailReport: "विस्तृत रिपोर्ट ईमेल करें",
        analyzing: "विश्लेषण हो रहा है...",
        sending: "भेजा जा रहा है...",
        missingTitle: "अधूरी जानकारी",
        missingDesc: "कृपया भरें: {{fields}}",
        emailRequiredTitle: "ईमेल आवश्यक",
        emailRequiredDesc: "विस्तृत रिपोर्ट प्राप्त करने के लिए कृपया अपना ईमेल दर्ज करें।",
        successTitle: "AI मूल्यांकन पूर्ण!",
        successDesc:
          "उन्नत AI और वास्तविक बाज़ार डेटा का उपयोग कर आपकी प्रॉपर्टी का विश्लेषण किया गया है।",
        errorTitle: "त्रुटि",
        errorDesc: "मूल्यांकन की गणना में विफल। कृपया पुनः प्रयास करें।",
      },
      cities: {
        explore: "प्रॉपर्टी देखें",
        primeLocations: "{{city}} के प्रमुख स्थान",
        mumbai: {
          title: "मुंबई में प्रॉपर्टी",
          tagline: "भारत की वित्तीय राजधानी",
          subtitle:
            "मुंबई के प्रमुख क्षेत्रों में सत्यापित प्रॉपर्टी खोजें। बांद्रा के लक्ज़री अपार्टमेंट से लेकर पवई के आधुनिक फ्लैट्स तक — AI-संचालित खोज और शून्य ब्रोकरेज के साथ अपना सही घर पाएँ।",
          ctaButton: "मुंबई की प्रॉपर्टी देखें",
          metaTitle: "मुंबई में प्रॉपर्टी - खरीदें, बेचें और किराए पर लें | RealtyBlocks",
          metaDesc:
            "मुंबई में सत्यापित प्रॉपर्टी खोजें। बांद्रा, अंधेरी, पवई में अपार्टमेंट खरीदें। वर्ली, लोअर परेल में फ्लैट किराए पर लें। शून्य ब्रोकरेज के साथ AI-संचालित खोज।",
        },
        bangalore: {
          title: "बैंगलोर में प्रॉपर्टी",
          tagline: "भारत की सिलिकॉन वैली",
          subtitle:
            "बैंगलोर के टेक कॉरिडोर में सत्यापित प्रॉपर्टी खोजें। कोरमंगला के आधुनिक अपार्टमेंट से लेकर व्हाइटफ़ील्ड के लक्ज़री विला तक — AI-संचालित खोज के साथ अपना सही घर पाएँ।",
          ctaButton: "बैंगलोर की प्रॉपर्टी देखें",
          metaTitle: "बैंगलोर में प्रॉपर्टी - खरीदें, बेचें और किराए पर लें | RealtyBlocks",
          metaDesc:
            "बैंगलोर में सत्यापित प्रॉपर्टी खोजें। कोरमंगला, व्हाइटफ़ील्ड, HSR लेआउट में अपार्टमेंट खरीदें। शून्य ब्रोकरेज के साथ AI-संचालित खोज।",
        },
        hyderabad: {
          title: "हैदराबाद में प्रॉपर्टी",
          tagline: "मोतियों और तकनीक का शहर",
          subtitle:
            "हैदराबाद के प्रमुख क्षेत्रों में सत्यापित प्रॉपर्टी खोजें। गाचीबोवली के आधुनिक अपार्टमेंट से लेकर जुबली हिल्स के लक्ज़री घरों तक — AI-संचालित खोज के साथ अपना सही घर पाएँ।",
          ctaButton: "हैदराबाद की प्रॉपर्टी देखें",
          metaTitle: "हैदराबाद में प्रॉपर्टी - खरीदें, बेचें और किराए पर लें | RealtyBlocks",
          metaDesc:
            "हैदराबाद में सत्यापित प्रॉपर्टी खोजें। गाचीबोवली, माधापुर, हाईटेक सिटी में अपार्टमेंट खरीदें। शून्य ब्रोकरेज के साथ AI-संचालित खोज।",
        },
      },
      seo: {
        homeTitle: "RealtyBlocks – भारत का AI-संचालित रियल एस्टेट OS",
        homeDesc:
          "AI-संचालित टूल्स, तत्काल डील्स और खरीदारों, विक्रेताओं, NRIs व एजेंटों के लिए विश्वसनीय सेवाओं के साथ पूरे भारत में सत्यापित प्रॉपर्टी खोजें, खरीदें, बेचें और किराए पर लें।",
      },
      legal: {
        viewHindi: "हिन्दी में पढ़ें",
        viewEnglish: "अंग्रेज़ी में देखें",
        privacy: "गोपनीयता नीति",
        terms: "सेवा की शर्तें",
        cookies: "कुकी नीति",
        disclaimer: "अस्वीकरण",
        refund: "रिफंड एवं रद्दीकरण",
        grievance: "शिकायत निवारण",
        security: "सुरक्षा",
        contactDpo: "हमारे शिकायत अधिकारी / DPO से संपर्क करें",
      },
    },
  },
};

const STORAGE_KEY = "propgauge_lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "hi"],
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    returnObjects: true,
  });

if (typeof document !== "undefined") {
  document.documentElement.lang = i18n.language?.startsWith("hi") ? "hi" : "en";
  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng?.startsWith("hi") ? "hi" : "en";
  });
}

export default i18n;
